/**
 * Windowed-connection prefetch store
 *
 * react-query's `prefetchQuery`, but for the entity grids' windowed connection.
 *
 * WHY A SEPARATE STORE (not the Apollo cache): the grids fetch pages via
 * `useWindowedConnection`, which uses `client.query({ fetchPolicy: 'no-cache' })`
 * on purpose (see use-windowed-connection.ts header — offset-paged deep scans would
 * otherwise grow the Apollo cache unboundedly, and the sparse-index machinery has no
 * cache-eviction hooks). Because it neither reads nor writes the Apollo normalized
 * cache, a `network-only` prefetch into that cache would be a silent no-op for the
 * grid. So instead we keep a tiny, self-contained store of just the FIRST page for a
 * given (query, filter, sort, pageSize), which `reset()` seeds synchronously for an
 * instant first paint. The hook then still runs its normal network fetch to
 * revalidate — mirroring `cache-and-network` semantics without touching Apollo's
 * cache or the shared `no-cache` fetch policy every grid relies on.
 *
 * Entries are short-lived (TTL) and single-use-ish: seeding does not delete the entry
 * (a remount within the TTL still gets the instant paint), but stale entries are
 * pruned lazily on read. This exists to smooth the Lead-Finder "build → open list"
 * hand-off; it is intentionally best-effort and never authoritative.
 */

import type { ApolloClient, DocumentNode } from '@apollo/client';

/** How long a prefetched page-0 stays eligible to seed a mount. */
export const WINDOWED_PREFETCH_TTL_MS = 60_000;

interface PrefetchEntry<TNode = unknown> {
  nodes: TNode[];
  totalCount: number | null;
  /** True when the prefetch returned fewer rows than a full page (dataset end). */
  reachedEnd: boolean;
  storedAt: number;
}

const store = new Map<string, PrefetchEntry>();

/**
 * In-flight prefetch requests, keyed the same as `store`. Lets a mounting grid
 * ATTACH to a prefetch that started (e.g. at build completion) but hasn't resolved
 * yet, instead of firing a duplicate identical query and waiting on that. The
 * promise resolves `true` once the entry is stored (rows present), `false` otherwise.
 */
const inFlight = new Map<string, Promise<boolean>>();

/** Register an in-flight prefetch promise for `key` (cleared when it settles). */
export function setWindowedPrefetchInFlight(key: string, p: Promise<boolean>): void {
  inFlight.set(key, p);
  void p.finally(() => {
    if (inFlight.get(key) === p) inFlight.delete(key);
  });
}

/** The in-flight prefetch promise for `key`, if one is running. */
export function readWindowedPrefetchInFlight(key: string): Promise<boolean> | undefined {
  return inFlight.get(key);
}

/**
 * Stable key for a windowed page-0. The query is identified by its first operation
 * name (GetContacts / GetAccounts / …); filter + sort + pageSize are JSON-stringified.
 * Key stability matters: the seed is only read when the mounting connection's config
 * hashes to the same key, so the shapes MUST match what the grid will request.
 */
export function windowedPrefetchKey(args: {
  query: DocumentNode;
  filter: Record<string, unknown>;
  sort: unknown[];
  pageSize: number;
}): string {
  const opName =
    args.query.definitions.find(
      (d): d is typeof d & { name?: { value: string } } =>
        d.kind === 'OperationDefinition',
    )?.name?.value ?? 'anon';
  return JSON.stringify({
    op: opName,
    filter: args.filter,
    sort: args.sort,
    pageSize: args.pageSize,
  });
}

export function storeWindowedPrefetch<TNode>(
  key: string,
  entry: Omit<PrefetchEntry<TNode>, 'storedAt'>,
): void {
  store.set(key, { ...entry, storedAt: Date.now() });
}

/**
 * Read (without deleting) a fresh prefetch entry for `key`, pruning it if expired.
 * Returns `undefined` when there is no fresh entry.
 */
export function readWindowedPrefetch<TNode>(
  key: string,
): Omit<PrefetchEntry<TNode>, 'storedAt'> | undefined {
  const entry = store.get(key) as PrefetchEntry<TNode> | undefined;
  if (!entry) return undefined;
  if (Date.now() - entry.storedAt > WINDOWED_PREFETCH_TTL_MS) {
    store.delete(key);
    return undefined;
  }
  const { nodes, totalCount, reachedEnd } = entry;
  return { nodes, totalCount, reachedEnd };
}

/** Test/util escape hatch. */
export function clearWindowedPrefetch(): void {
  store.clear();
}

interface Edge<TNode> {
  node?: TNode | null;
}

export interface PrefetchWindowedFirstPageArgs<TData, TNode> {
  client: ApolloClient<object>;
  query: DocumentNode;
  /** Full filter — MUST match the grid's filter exactly (incl. workspaceId + listMembership). */
  filter: Record<string, unknown>;
  /** MUST match the grid's default sort exactly. */
  sort: unknown[];
  /** MUST match the grid's page size (WINDOWED_SCROLL_PAGE_SIZE). */
  pageSize: number;
  /** Pull { edges, totalCount } out of the query result (mirrors the hook's `extract`). */
  extract: (
    data: TData,
  ) => { edges: Array<Edge<TNode> | null>; totalCount?: number | null } | undefined;
}

/**
 * Fire-and-forget prefetch of a windowed connection's FIRST page.
 *
 * Runs the SAME query + variables the grid's page 0 will run
 * (`{ filter, sort, first: pageSize, after: "0" }`, `fetchPolicy: 'network-only'`),
 * and stashes the resulting nodes in the module store so a subsequent grid mount
 * paints them instantly (see readWindowedPrefetch / useWindowedConnection.reset).
 *
 * Best-effort by contract: `errorPolicy: 'ignore'`, fully try/caught, never throws,
 * never returns a rejected promise. Safe to call unawaited from a completion handler.
 * A freshly direct-built list may not be query-returnable yet (OpenSearch refresh
 * lag) — an empty/failed result simply stores nothing and the grid loads normally.
 */
export function prefetchWindowedFirstPage<TData, TNode>(
  args: PrefetchWindowedFirstPageArgs<TData, TNode>,
): Promise<boolean> {
  const { client, query, filter, sort, pageSize, extract } = args;
  const key = windowedPrefetchKey({ query, filter, sort, pageSize });
  const run = async (): Promise<boolean> => {
    try {
      const res = await client.query<TData>({
        query,
        variables: { filter, sort, first: pageSize, after: '0' },
        fetchPolicy: 'network-only',
        errorPolicy: 'ignore',
      });
      if (!res?.data) return false;
      const extracted = extract(res.data);
      if (!extracted) return false;
      const nodes = (extracted.edges ?? [])
        .map((e) => e?.node)
        .filter((n): n is TNode => !!n);
      if (nodes.length === 0) return false; // nothing indexed yet — caller may retry
      storeWindowedPrefetch<TNode>(key, {
        nodes,
        totalCount:
          typeof extracted.totalCount === 'number' ? extracted.totalCount : null,
        reachedEnd: nodes.length < pageSize,
      });
      return true;
    } catch {
      // Swallow — prefetch must never affect the build/navigation UX.
      return false;
    }
  };
  // Publish the in-flight promise so a grid mounting mid-request can attach to it
  // instead of firing a duplicate identical query (see useWindowedConnection.reset).
  const p = run();
  setWindowedPrefetchInFlight(key, p);
  return p;
}
